// src/components/order/OrderBaseTable.tsx

import {
  Card,
  CardContent,
  Box,
  Grid,
  Typography,
  Chip,
  useTheme,
} from '@mui/material';
import { ReactNode, isValidElement } from 'react';
import { OrderResponse } from '@apis/order';
import { useRecoilValue } from 'recoil';
import { userState } from '../../recoils/atoms';
import { OrderStatus } from '@models/order';
import { ProductSaleType } from '@apis/product';
import { utcToZonedTime, format as formatTz } from 'date-fns-tz';
import { format as formatLocalDate } from 'date-fns';
import { UserGroup } from '@models/user';

import {
  getOrderStatusMeta,
} from '@constants/orderLabels';
import { ProductSaleTypeDisplay } from '@components/product/ProductSaleTypeDisplay';

type Props = {
  order: OrderResponse;
};

/* ============================================================================
 *  MONEY FORMATTERS
 * ========================================================================== */

/**
 * Format amount as USD display string.
 * Returns "N/A" when amount is null/undefined.
 */
const formatUsd = (amount?: number | null) =>
  amount == null ? 'N/A' : `USD ${amount.toFixed(2)}`;

/* ============================================================================
 *  TIME FORMATTERS (KST + LOCAL)
 *  - KST: Asia/Seoul timezone
 *  - Local: browser timezone + offset
 * ========================================================================== */

/** Full datetime in KST: "yyyy-MM-dd HH:mm:ss KST" */
const formatKST = (ts: number | string) => {
  const d = new Date(ts);
  const kst = utcToZonedTime(d, 'Asia/Seoul');
  return `${formatTz(kst, 'yyyy-MM-dd HH:mm:ss')} KST`;
};

/** Date-only in KST: "yyyy-MM-dd KST" */
const formatKSTDateOnly = (ts: number | string) => {
  const d = new Date(ts);
  const kst = utcToZonedTime(d, 'Asia/Seoul');
  return `${formatTz(kst, 'yyyy-MM-dd')} KST`;
};

/** Local timezone offset like "UTC+09:00" */
const getLocalOffset = (ts: number | string) => {
  const d = new Date(ts);
  const offset = -d.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `UTC${sign}${hh}:${mm}`;
};

/** Local datetime with offset and timezone id */
const formatLocal = (ts: number | string) => {
  const d = new Date(ts);
  const dt = formatLocalDate(d, 'yyyy-MM-dd HH:mm:ss');
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `${dt} ${getLocalOffset(ts)} (${tz})`;
};

/**
 * Combined KST + Local display block.
 * - First line: KST
 * - Second line: local time with offset
 */
const renderDateWithLocal = (ts: number | string) => (
  <Box>
    <Typography variant="body1" fontWeight={500}>
      {formatKST(ts)}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {formatLocal(ts)}
    </Typography>
  </Box>
);

/* ============================================================================
 *  GENERIC FIELD RENDERERS
 * ========================================================================== */

/**
 * Standard labeled text field.
 * - Label in small uppercase, value below
 * - Handles string/ReactNode/N/A
 */
const renderField = (label: string, value: ReactNode) => (
  <Box>
    <Typography
      variant="caption"
      fontWeight={600}
      color="text.secondary"
      sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.4 }}
    >
      {label}
    </Typography>

    {isValidElement(value) ? (
      value
    ) : (
      <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
        {value ?? 'N/A'}
      </Typography>
    )}
  </Box>
);

/**
 * Labeled field for Chip-like contents.
 */
const renderChipField = (label: string, chip: ReactNode) => (
  <Box>
    <Typography
      variant="caption"
      fontWeight={600}
      color="text.secondary"
      sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.4 }}
    >
      {label}
    </Typography>
    <Box sx={{ mt: 0.5 }}>{chip}</Box>
  </Box>
);

/**
 * Order status chip using shared label/color policy.
 */
const renderStatusChip = (status: OrderStatus | string) => {
  const meta = getOrderStatusMeta(status);

  return renderChipField(
    'Order Status',
    <Chip
      label={meta.label}
      color={meta.chip.color}
      variant={meta.chip.variant}
      size="small"
    />,
  );
};

/**
 * Product block: code + name
 */
const renderProductField = (product: OrderResponse['product']) => (
  <Box>
    <Typography
      variant="caption"
      fontWeight={600}
      color="text.secondary"
      sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.4 }}
    >
      Product
    </Typography>

    <Typography variant="body1" fontWeight={600}>
      {product.productCode}
    </Typography>
    <Typography variant="body2">{product.productName}</Typography>
  </Box>
);

type GroupItem = { label: string; value: ReactNode };

/**
 * Simple stacked key-value group (e.g. Order, Refund)
 */
const renderGroup = (title: string, items: GroupItem[]) => (
  <Box>
    <Typography
      variant="caption"
      fontWeight={600}
      color="text.secondary"
      sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.4 }}
    >
      {title}
    </Typography>

    {items.map((item) => (
      <Typography key={item.label} variant="body2">
        <strong>{item.label}:</strong> {item.value}
      </Typography>
    ))}
  </Box>
);

/* ============================================================================
 *  MAIN COMPONENT
 * ========================================================================== */

/**
 * OrderBaseTable
 * - Summary header block for an order detail page
 * - Shows key order info: type, status, dates, product, stock, refund, partner
 */
const OrderBaseTable = ({ order }: Props) => {
  const user = useRecoilValue(userState);
  const theme = useTheme();

  const isPartnerUser = user?.group === UserGroup.PARTNER;
  const showStockCode = !isPartnerUser;
  const isReservation =
    order.product.productSaleType === ProductSaleType.RESERVATION;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent sx={{ pt: 2, pb: 2 }}>
        {/* Row 1: Order Code / Type / Status / Order Date */}
        <Grid
          container
          spacing={2}
          columns={{ xs: 4, sm: 8, md: 12 }}
          sx={{ mb: 1 }}
        >
          <Grid item xs={4} sm={4} md={3}>
            {renderField('Order Code', order.orderCode)}
          </Grid>

          <Grid item xs={4} sm={4} md={3}>
            {renderChipField(
              'Order Type',
              <ProductSaleTypeDisplay saleType={order.product.productSaleType} />,
            )}
          </Grid>

          <Grid item xs={4} sm={4} md={3}>
            {renderStatusChip(order.orderStatus)}
          </Grid>

          <Grid item xs={4} sm={8} md={3}>
            {renderField('Order Date', renderDateWithLocal(order.createdAt))}
          </Grid>
        </Grid>

        {/* Row 2: Product / Stock / Order summary / Refund summary */}
        <Grid
          container
          spacing={2}
          columns={{ xs: 4, sm: 8, md: 12 }}
          sx={{ mb: 1 }}
        >
          {/* Product info */}
          <Grid item xs={4} sm={8} md={3}>
            {renderProductField(order.product)}
          </Grid>

          {/* Stock code (hidden for partner users) */}
          {showStockCode && (
            <Grid item xs={4} sm={4} md={3}>
              {renderChipField(
                'Stock Code',
                <Chip
                  label={order.stock.stockCode}
                  color="error"
                  size="small"
                  variant="outlined"
                />,
              )}
            </Grid>
          )}

          {/* Order summary (quantity + amount) */}
          <Grid item xs={4} sm={4} md={3}>
            {renderGroup('Order', [
              { label: 'Quantity', value: order.quantity },
              { label: 'Amount', value: formatUsd(order.paymentAmount) },
            ])}
          </Grid>

          {/* Refund summary (quantity + amount) */}
          <Grid item xs={4} sm={4} md={3}>
            {renderGroup('Refund', [
              { label: 'Quantity', value: order.refundQuantity },
              { label: 'Amount', value: formatUsd(order.refundAmount) },
            ])}
          </Grid>
        </Grid>

        {/* Row 3: Partner / Partner Order ID / Supplier Order ID / Reserved Date */}
        <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>
          {/* Partner */}
          <Grid item xs={4} sm={4} md={3}>
            {renderChipField(
              'Partner',
              <Chip
                label={order.partner.name}
                color="primary"
                size="small"
                variant="outlined"
              />,
            )}
          </Grid>

          {/* Partner Order ID */}
          <Grid item xs={4} sm={4} md={3}>
            {renderField(
              'Partner Order ID',
              order.externalReserveCode || '-',
            )}
          </Grid>

          {/* Supplier Order ID */}
          <Grid item xs={4} sm={4} md={3}>
            {renderField('Supplier Order ID', order.externalOrderId || '-')}
          </Grid>

          {/* Reserved Date (KST, date only) - only for Reservation orders */}
          {isReservation && (
            <Grid item xs={4} sm={4} md={3}>
              {renderField(
                'Reserved Date (KST)',
                order.reservedAt ? formatKSTDateOnly(order.reservedAt) : '-',
              )}
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default OrderBaseTable;
